import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';

const { Option } = Select;

/**
 * Autocomplete pour rechercher un sinistre par numéro, assuré, immatriculation
 * ou police. La recherche interroge le backend (`GET /v1/sinistres?search=…`)
 * et porte donc sur toute la base - pas seulement une page chargée en mémoire.
 * Props:
 *   value, onChange - contrôlé (trackingId string)
 *   onSelect(trackingId, sinistreObject) - callback avec l'objet complet
 *   placeholder
 *   style
 *   extraParams - filtres supplémentaires fusionnés dans la requête GET
 *     (ex. { typeSinistre: 'SURVENU_TOGO' }) ; le backend les supporte déjà
 *     sur /v1/sinistres. Le composant appelant doit en stabiliser l'identité
 *     (useMemo) pour ne pas redéfinir la recherche à chaque rendu.
 *   excludeTrackingIds - trackingIds à retirer des résultats après coup (le
 *     backend n'a pas de paramètre d'exclusion) ; typiquement le dossier
 *     courant, pour ne pas se proposer lui-même comme cible.
 */
export default function SinistreSearch({
  value, onChange, onSelect, placeholder, style, extraParams, excludeTrackingIds,
}) {
  const { t } = useTranslation('commun');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSinistre, setSelectedSinistre] = useState(null);
  const debounceRef = useRef(null);
  // Numéro de requête : seule la réponse de la dernière recherche est retenue,
  // les réponses en retard d'une saisie précédente sont ignorées.
  const requeteRef = useRef(0);

  // Quand `value` est posé de l'extérieur (query string, ligne pré-remplie…),
  // résoudre le libellé via le détail - sinon AntD affiche l'UUID brut.
  useEffect(() => {
    if (!value) { setSelectedSinistre(null); return; }
    if (selectedSinistre?.sinistreTrackingId === value) return;
    const local = options.find((s) => s.sinistreTrackingId === value);
    if (local) { setSelectedSinistre(local); return; }
    let annule = false;
    axiosInstance.get(`/v1/sinistres/${value}`)
      .then((r) => { if (!annule && r.data?.data) setSelectedSinistre(r.data.data); })
      .catch(() => { });
    return () => { annule = true; };
  }, [value, options, selectedSinistre]);

  const handleSearch = useCallback((term) => {
    clearTimeout(debounceRef.current);
    const t = term?.trim();
    if (!t || t.length < 2) { setOptions([]); return; }
    debounceRef.current = setTimeout(async () => {
      const idRequete = ++requeteRef.current;
      setLoading(true);
      try {
        const res = await axiosInstance.get('/v1/sinistres', { params: { page: 0, size: 20, search: t, ...extraParams } });
        // PaginatedResponse backend : la liste est directement dans `data`
        // (pas dans `data.content` comme une Page Spring brute).
        const d = res.data?.data;
        const liste = Array.isArray(d) ? d : d?.content || [];
        const filtree = excludeTrackingIds?.length
          ? liste.filter((s) => !excludeTrackingIds.includes(s.sinistreTrackingId))
          : liste;
        if (idRequete === requeteRef.current) setOptions(filtree);
      } catch {
        if (idRequete === requeteRef.current) setOptions([]);
      } finally {
        if (idRequete === requeteRef.current) setLoading(false);
      }
    }, 300);
  }, [extraParams, excludeTrackingIds]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleSelect = (trackingId) => {
    const found = options.find((s) => s.sinistreTrackingId === trackingId) || selectedSinistre;
    if (found) setSelectedSinistre(found);
    if (onSelect && found) onSelect(trackingId, found);
  };

  // Toujours inclure le sinistre sélectionné dans les options rendues,
  // pour que AntD trouve le label correspondant à `value`.
  const displayOptions = useMemo(() => {
    if (!selectedSinistre) return options;
    if (options.find((o) => o.sinistreTrackingId === selectedSinistre.sinistreTrackingId)) return options;
    return [selectedSinistre, ...options];
  }, [options, selectedSinistre]);

  return (
    <Select
      showSearch
      filterOption={false}
      value={value}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={(v) => {
        if (!v) setSelectedSinistre(null);
        if (onChange) onChange(v);
      }}
      optionLabelProp="label"
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : t('sinistreSearch.aucunResultat')}
      placeholder={placeholder || t('sinistreSearch.placeholder')}
      style={{ width: '100%', ...style }}
      allowClear
    >
      {displayOptions.map((s) => (
        <Option
          key={s.sinistreTrackingId}
          value={s.sinistreTrackingId}
          label={s.numeroSinistreLocal}
        >
          <span style={{ fontWeight: 600, color: '#1a6b3c', fontFamily: 'var(--font-main)' }}>
            {s.numeroSinistreLocal}
          </span>
          {(s.assureNomComplet || s.nomAssure) && (
            <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>
              {s.assureNomComplet || s.nomAssure}
            </span>
          )}
        </Option>
      ))}
    </Select>
  );
}
